const OWNER_DIST = 'owner_distribution';

export const CASH_FLOW_METHODS = [
  {
    value: 'trailing_12_calendar',
    label: 'Trailing 12M Avg',
    detail: 'Average actual income and operating expenses across the last 12 calendar months.',
    formula: 'Cash Flow = (Last 12M Income / 12) - (Last 12M Operating Expenses / 12) - Debt Service',
  },
  {
    value: 'trailing_12_observed',
    label: 'Observed Months Avg',
    detail: 'Average actual income and operating expenses across only the months with ledger activity in the last 12 months.',
    formula: 'Cash Flow = (Last 12M Income / Observed Months) - (Last 12M Operating Expenses / Observed Months) - Debt Service',
  },
  {
    value: 'latest_month',
    label: 'Latest Actual Month',
    detail: 'Use the most recent month with ledger activity.',
    formula: 'Cash Flow = Latest Month Income - Latest Month Operating Expenses - Debt Service',
  },
  {
    value: 'market_rent_underwrite',
    label: 'Market Rent Underwrite',
    detail: 'Use market rent as income and recent operating expenses as the cost basis.',
    formula: 'Cash Flow = Market Rent Estimate - Avg Operating Expenses - Debt Service',
  },
];

export const RETURN_METHODS = [
  {
    value: 'purchase_price',
    label: 'Yield on Cost',
    detail: 'Annual cash flow divided by original purchase price.',
    denominatorLabel: 'Original Purchase Price',
    formula: 'Yield on Cost = Annual Cash Flow / Original Purchase Price',
  },
  {
    value: 'current_value',
    label: 'Value Yield',
    detail: 'Annual cash flow divided by current estimated value.',
    denominatorLabel: 'Current Value',
    formula: 'Return = (Monthly Cash Flow x 12) / Current Value',
  },
  {
    value: 'current_equity',
    label: 'Equity Yield',
    detail: 'Annual cash flow divided by estimated current equity.',
    denominatorLabel: 'Current Equity',
    formula: 'Return = (Monthly Cash Flow x 12) / Current Equity',
  },
];

export const DEFAULT_METRIC_SETTINGS = Object.freeze({
  cashFlowMethod: CASH_FLOW_METHODS[0].value,
  returnMethod: RETURN_METHODS[0].value,
  includeDebtService: true,
});

const cashFlowMethodMap = Object.fromEntries(CASH_FLOW_METHODS.map((method) => [method.value, method]));
const returnMethodMap = Object.fromEntries(RETURN_METHODS.map((method) => [method.value, method]));

const roundMoney = (value) => (Number.isFinite(value) ? Math.round(value * 100) / 100 : null);

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toPositiveNumberOrNull(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toMonthKey(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function toMonthDate(month) {
  return new Date(`${month}-01T00:00:00`);
}

function formatMonthLabel(month) {
  if (!month) return null;
  return new Date(`${month}-02T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function sumAmounts(values) {
  return values.reduce((sum, value) => sum + value, 0);
}

function buildMonthlySummaries(income = [], expenses = []) {
  const summaries = new Map();

  function getSummary(month) {
    if (!summaries.has(month)) {
      summaries.set(month, { month, income: 0, expenses: 0 });
    }
    return summaries.get(month);
  }

  income.forEach((row) => {
    const month = toMonthKey(row.period_month);
    if (!month) return;
    getSummary(month).income += toNumber(row.amount);
  });

  expenses.forEach((row) => {
    const month = toMonthKey(row.expense_date);
    if (!month) return;
    if (row.category === OWNER_DIST) return;
    getSummary(month).expenses += toNumber(row.amount);
  });

  return Array.from(summaries.values()).sort((left, right) => left.month.localeCompare(right.month));
}

function trailingTwelveEntries(entries) {
  const cutoff = new Date();
  cutoff.setDate(1);
  cutoff.setMonth(cutoff.getMonth() - 11);
  return entries.filter((entry) => toMonthDate(entry.month) >= cutoff);
}

export function normalizeMetricSettings(settings = {}) {
  const next = { ...DEFAULT_METRIC_SETTINGS, ...settings };
  if (!cashFlowMethodMap[next.cashFlowMethod]) next.cashFlowMethod = DEFAULT_METRIC_SETTINGS.cashFlowMethod;
  if (!returnMethodMap[next.returnMethod]) next.returnMethod = DEFAULT_METRIC_SETTINGS.returnMethod;
  next.includeDebtService = Boolean(next.includeDebtService);
  return next;
}

export function calculatePropertyMetrics({
  property,
  income = [],
  expenses = [],
  monthlyDebtService = 0,
  remainingBalance = null,
  settings = DEFAULT_METRIC_SETTINGS,
}) {
  const normalizedSettings = normalizeMetricSettings(settings);
  const cashFlowMethod = cashFlowMethodMap[normalizedSettings.cashFlowMethod];
  const returnMethod = returnMethodMap[normalizedSettings.returnMethod];
  const monthlySummaries = buildMonthlySummaries(income, expenses);
  const trailingEntries = trailingTwelveEntries(monthlySummaries);
  const latestEntry = monthlySummaries.length > 0 ? monthlySummaries[monthlySummaries.length - 1] : null;
  const observedMonths = trailingEntries.length;
  const trailingIncomeTotal = sumAmounts(trailingEntries.map((entry) => entry.income));
  const trailingExpenseTotal = sumAmounts(trailingEntries.map((entry) => entry.expenses));
  const totalOperatingExpenses = sumAmounts(monthlySummaries.map((entry) => entry.expenses));
  const totalOwnerDistributions = sumAmounts(
    expenses
      .filter((row) => row.category === OWNER_DIST)
      .map((row) => toNumber(row.amount))
  );
  const marketRent = toPositiveNumberOrNull(property?.current_rent_estimate);
  const purchasePrice = toPositiveNumberOrNull(property?.purchase_price);
  const currentValue = toPositiveNumberOrNull(property?.current_value);
  const currentEquity = currentValue != null && remainingBalance != null
    ? Math.max(0, currentValue - toNumber(remainingBalance))
    : null;
  const debtServiceUsed = normalizedSettings.includeDebtService ? toNumber(monthlyDebtService) : 0;

  let incomeInput = null;
  let expenseInput = null;
  let incomeLabel = '';
  let expenseLabel = '';
  const notes = ['Owner distributions are excluded from operating expense and return calculations.'];

  switch (cashFlowMethod.value) {
    case 'trailing_12_calendar':
      if (trailingEntries.length > 0) {
        incomeInput = trailingIncomeTotal / 12;
        expenseInput = trailingExpenseTotal / 12;
        incomeLabel = 'Avg monthly income (last 12 calendar months)';
        expenseLabel = 'Avg monthly operating expenses (last 12 calendar months)';
      } else {
        notes.push('No ledger activity was found in the last 12 months for the selected actuals formula.');
      }
      break;
    case 'trailing_12_observed':
      if (observedMonths > 0) {
        incomeInput = trailingIncomeTotal / observedMonths;
        expenseInput = trailingExpenseTotal / observedMonths;
        incomeLabel = `Avg monthly income (${observedMonths} observed month${observedMonths === 1 ? '' : 's'})`;
        expenseLabel = `Avg monthly operating expenses (${observedMonths} observed month${observedMonths === 1 ? '' : 's'})`;
        notes.push(`Observed-month average uses ${observedMonths} ledger month${observedMonths === 1 ? '' : 's'} from the last 12 months.`);
      } else {
        notes.push('No ledger activity was found in the last 12 months for the observed-month formula.');
      }
      break;
    case 'latest_month':
      if (latestEntry) {
        incomeInput = latestEntry.income;
        expenseInput = latestEntry.expenses;
        incomeLabel = `Income (${formatMonthLabel(latestEntry.month)})`;
        expenseLabel = `Operating expenses (${formatMonthLabel(latestEntry.month)})`;
        notes.push(`Latest actual month uses ledger totals from ${formatMonthLabel(latestEntry.month)}.`);
      } else {
        notes.push('No ledger activity is available for the latest-month formula.');
      }
      break;
    case 'market_rent_underwrite':
      if (marketRent != null) {
        incomeInput = marketRent;
        expenseInput = observedMonths > 0 ? trailingExpenseTotal / observedMonths : 0;
        incomeLabel = 'Market rent estimate';
        expenseLabel = observedMonths > 0
          ? `Avg monthly operating expenses (${observedMonths} observed month${observedMonths === 1 ? '' : 's'})`
          : 'Avg monthly operating expenses';
        notes.push(observedMonths > 0
          ? `Market-rent underwriting uses operating expenses averaged across ${observedMonths} observed month${observedMonths === 1 ? '' : 's'}.`
          : 'No recent operating expenses were found, so market-rent underwriting currently assumes $0 monthly operating expense.');
      } else {
        notes.push('Market rent estimate is required for the market-rent underwriting formula.');
      }
      break;
    default:
      break;
  }

  const monthlyCashFlow = incomeInput != null && expenseInput != null
    ? roundMoney(incomeInput - expenseInput - debtServiceUsed)
    : null;
  const annualCashFlow = monthlyCashFlow != null ? roundMoney(monthlyCashFlow * 12) : null;

  let denominatorValue = null;
  switch (returnMethod.value) {
    case 'purchase_price':
      denominatorValue = purchasePrice;
      if (denominatorValue == null) notes.push('Purchase price is required for the purchase-price return formula.');
      break;
    case 'current_value':
      denominatorValue = currentValue;
      if (denominatorValue == null) notes.push('Current value is required for the current-value return formula.');
      break;
    case 'current_equity':
      denominatorValue = currentEquity && currentEquity > 0 ? currentEquity : null;
      if (denominatorValue == null) notes.push('Current value and remaining loan balance are required for the equity-yield formula.');
      break;
    default:
      break;
  }

  const returnRate = annualCashFlow != null && denominatorValue != null
    ? roundMoney((annualCashFlow / denominatorValue) * 100)
    : null;

  return {
    settings: normalizedSettings,
    cashFlowMethod,
    returnMethod,
    monthlySummaries,
    observedMonths,
    latestMonthLabel: latestEntry ? formatMonthLabel(latestEntry.month) : null,
    totalOperatingExpenses: roundMoney(totalOperatingExpenses) || 0,
    totalOwnerDistributions: roundMoney(totalOwnerDistributions) || 0,
    trailing12CalendarIncome: trailingEntries.length > 0 ? roundMoney(trailingIncomeTotal / 12) : null,
    trailing12CalendarExpenses: trailingEntries.length > 0 ? roundMoney(trailingExpenseTotal / 12) : null,
    incomeInput: incomeInput != null ? roundMoney(incomeInput) : null,
    expenseInput: expenseInput != null ? roundMoney(expenseInput) : null,
    debtServiceInput: roundMoney(debtServiceUsed) || 0,
    incomeLabel,
    expenseLabel,
    monthlyCashFlow,
    annualCashFlow,
    denominatorLabel: returnMethod.denominatorLabel,
    denominatorValue: denominatorValue != null ? roundMoney(denominatorValue) : null,
    returnRate,
    notes,
  };
}
